import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { Save2, Setting2 } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage } from '../api/client';
import { ErrorBlock, LoadingBlock } from '../components/StateBlock';
import { WorkspaceSection } from '../workspace/WorkspaceSection';
import { t } from '../i18n';

type Provider = {
  id: string;
  code: string;
  name: string;
  payment_method: string;
  integration_type: string;
  capabilities_json?: string[];
  active: boolean;
};

type MerchantConnection = {
  id?: string;
  payment_provider_id: string;
  display_name?: string;
  external_merchant_id?: string;
  status: string;
  active: boolean;
  provider?: Provider;
  // Provider-specific credentials stored as JSON on the backend.
  // Secrets (client_secret) are masked as '••••••••' by the API on read.
  configuration_json?: Record<string, string | boolean>;
};

export function PaymentProvidersPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [connections, setConnections] = useState<MerchantConnection[]>([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [providersRes] = await Promise.all([
          adminApi.get('payment-providers').catch(() => null),
        ]);

        if (cancelled) return;

        const providerData = providersRes?.data;
        if (Array.isArray(providerData?.data)) {
          setProviders(providerData.data);
        } else if (Array.isArray(providerData)) {
          setProviders(providerData);
        }

        // Load merchant connections
        const connRes = await adminApi.get('merchant-payment-providers').catch(() => null);
        const connData = connRes?.data;
        if (Array.isArray(connData?.data)) {
          setConnections(connData.data);
        } else if (Array.isArray(connData)) {
          setConnections(connData);
        }
      } catch (err) {
        setError(apiErrorMessage(err, t('common.unableToLoad', { name: t('paymentProviders.title') })));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getConnection = (providerId: string) =>
    connections.find((c) => c.payment_provider_id === providerId);

  const updateConnection = (providerId: string, field: string, value: unknown) => {
    setConnections((prev) => {
      const existing = prev.find((c) => c.payment_provider_id === providerId);
      if (existing) {
        return prev.map((c) =>
          c.payment_provider_id === providerId ? { ...c, [field]: value } : c
        );
      }
      // Create a new connection entry
      return [...prev, {
        payment_provider_id: providerId,
        display_name: '',
        external_merchant_id: '',
        status: 'PENDING',
        active: value as boolean,
        [field]: value,
      }];
    });
  };

  // Update a single key inside configuration_json (nested credentials object).
  const updateConfig = (providerId: string, key: string, value: string | boolean) => {
    setConnections((prev) => {
      const existing = prev.find((c) => c.payment_provider_id === providerId);
      const configKey = 'configuration_json';
      if (existing) {
        return prev.map((c) =>
          c.payment_provider_id === providerId
            ? { ...c, [configKey]: { ...(c.configuration_json ?? {}), [key]: value } }
            : c
        );
      }
      return [...prev, {
        payment_provider_id: providerId,
        display_name: '',
        external_merchant_id: '',
        status: 'PENDING',
        active: false,
        [configKey]: { [key]: value },
      }];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each connection
      for (const conn of connections) {
        const payload = {
          payment_provider_id: conn.payment_provider_id,
          display_name: conn.display_name ?? '',
          external_merchant_id: conn.external_merchant_id ?? '',
          status: conn.active ? 'CONNECTED' : 'DISCONNECTED',
          active: conn.active,
          configuration_json: conn.configuration_json ?? {},
        };
        if (conn.id) {
          await adminApi.put(`merchant-payment-providers/${conn.id}`, payload);
        } else {
          await adminApi.post('merchant-payment-providers', payload);
        }
      }
      setToast({ msg: t('paymentProviders.savedToast'), sev: 'success' });
    } catch (err) {
      setToast({ msg: apiErrorMessage(err, t('paymentProviders.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock message={error} />;

  // Cash is always available by default — it doesn't need configuration.
  // The POS get_payment_options endpoint includes cash as a hardcoded fallback.
  const activeProviders = providers.filter((p) => p.active && p.code !== 'CASH');
  const inactiveProviders = providers.filter((p) => !p.active && p.code !== 'CASH');

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Setting2 size={28} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('paymentProviders.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('paymentProviders.description')}
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info">
        {t('paymentProviders.info')}
      </Alert>

      {activeProviders.map((provider) => {
        const conn = getConnection(provider.id);
        const isEnabled = conn?.active ?? false;
        const isQrProvider = (provider.capabilities_json ?? []).includes('QR');
        return (
          <Card key={provider.id}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {provider.name}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip size="small" label={provider.payment_method} variant="outlined" />
                    <Chip size="small" label={provider.integration_type} variant="outlined" />
                    {isQrProvider ? <Chip size="small" label="QR" color="primary" /> : null}
                  </Stack>
                </Box>
                <Switch
                  checked={isEnabled}
                  onChange={(e) => updateConnection(provider.id, 'active', e.target.checked)}
                />
              </Stack>

              {isEnabled && (
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('paymentProviders.displayName')}
                    value={conn?.display_name ?? ''}
                    onChange={(e) => updateConnection(provider.id, 'display_name', e.target.value)}
                    placeholder={provider.name}
                  />
                  {provider.code === 'PAYPAL_QR' && (
                    <>
                      <Alert severity="info" sx={{ py: 0 }}>
                        {t('paymentProviders.paypalInfo')}
                      </Alert>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={conn?.configuration_json?.paypal_sandbox !== false}
                            onChange={(e) => updateConfig(provider.id, 'paypal_sandbox', e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {t('paymentProviders.sandboxMode')} {conn?.configuration_json?.paypal_sandbox === false ? t('paymentProviders.live') : t('paymentProviders.test')}
                          </Typography>
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label={t('paymentProviders.clientId')}
                        value={conn?.configuration_json?.paypal_client_id ?? ''}
                        onChange={(e) => updateConfig(provider.id, 'paypal_client_id', e.target.value)}
                        placeholder="AeA1QIZXiflr1_-r0lzxwxK7..."
                        helperText={t('paymentProviders.clientIdHelper')}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="password"
                        label={t('paymentProviders.clientSecret')}
                        value={(() => {
                          const secret = conn?.configuration_json?.paypal_client_secret;
                          return typeof secret === 'string' && !secret.includes('••') ? secret : '';
                        })()}
                        onChange={(e) => updateConfig(provider.id, 'paypal_client_secret', e.target.value)}
                        placeholder={typeof conn?.configuration_json?.paypal_client_secret === 'string' && conn.configuration_json.paypal_client_secret ? t('paymentProviders.clientSecretPlaceholder') : 'EGnHDxD_qRPdaLdH...'}
                        helperText={
                          typeof conn?.configuration_json?.paypal_client_secret === 'string' && conn.configuration_json.paypal_client_secret
                            ? t('paymentProviders.clientSecretSaved')
                            : t('paymentProviders.clientSecretHelper')
                        }
                      />
                    </>
                  )}
                  {(provider.code === 'MANUAL_CARD' || provider.code === 'BANK_TRANSFER_MANUAL') && (
                    <TextField
                      fullWidth
                      size="small"
                      label={t('paymentProviders.accountRef')}
                      value={conn?.external_merchant_id ?? ''}
                      onChange={(e) => updateConnection(provider.id, 'external_merchant_id', e.target.value)}
                      placeholder={t('paymentProviders.accountRefPlaceholder')}
                    />
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        );
      })}

      {inactiveProviders.length > 0 ? (
        <WorkspaceSection title={t('paymentProviders.comingSoon')} description={t('paymentProviders.comingSoonDesc')}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {inactiveProviders.map((p) => (
              <Chip key={p.id} label={p.name} variant="outlined" disabled />
            ))}
          </Stack>
        </WorkspaceSection>
      ) : null}

      <Card sx={{ position: 'sticky', bottom: 16, zIndex: 10 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'flex-end', '&:last-child': { pb: 1.5 } }}>
          <Button variant="contained" color="primary" disabled={saving} onClick={() => void handleSave()} startIcon={<Save2 size={18} />}>
            {saving ? t('action.saving') : t('common.saveSettings')}
          </Button>
        </CardContent>
      </Card>

      {toast ? (
        <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} autoHideDuration={4000} onClose={() => setToast(null)}>
          <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert>
        </Snackbar>
      ) : null}
    </Stack>
  );
}
